import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useBackgroundJobs } from '../context/BackgroundJobsContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const AdminEvents = () => {
  const { addDeletionJob } = useBackgroundJobs();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [changePinEvent, setChangePinEvent] = useState(null);
  const [changePinValue, setChangePinValue] = useState('');
  const [changePinError, setChangePinError] = useState('');
  const [deleteConfirmEvent, setDeleteConfirmEvent] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Banner state per card: { [eventId]: { uploading, error } }
  const [cardBanner, setCardBanner] = useState({});
  const cardBannerRefs = useRef({});

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/events/admin/all`);
      setEvents(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (!deleteConfirmEvent) return;
    setDeleting(true);
    try {
      const res = await axios.delete(`${API_URL}/events/admin/${deleteConfirmEvent.id}`);
      const batchList = res.data.batches || [];
      batchList.forEach((b) => {
        addDeletionJob({
          batchId: b.id, total: b.photo_count ?? 0,
          label: `Batch #${b.batch_number}${b.event_name ? ' - ' + b.event_name : ''}`,
          eventName: b.event_name || null,
        });
      });
      setEvents(events.filter(ev => ev.id !== deleteConfirmEvent.id));
      setDeleteConfirmEvent(null);
      alert(res.data.batches_queued > 0
        ? `Event deleted. ${res.data.batches_queued} batch(es) are being deleted in the background.`
        : 'Event deleted.');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete event');
    } finally {
      setDeleting(false);
    }
  };

  const handleChangePin = async (e) => {
    e.preventDefault();
    const pin = changePinValue.replace(/\D/g, '');
    if (pin.length !== 6) { setChangePinError('PIN must be exactly 6 digits'); return; }
    if (!changePinEvent) return;
    setChangePinError('');
    try {
      const res = await axios.patch(`${API_URL}/events/admin/${changePinEvent.id}/pin`, { pin });
      setEvents(events.map(ev => ev.id === changePinEvent.id ? { ...ev, pin: res.data.pin } : ev));
      setChangePinEvent(null); setChangePinValue('');
      alert('PIN updated.');
    } catch (err) {
      setChangePinError(err.response?.data?.error || 'Failed to update PIN');
    }
  };

  const toggleShowAllPhotoTab = async (ev) => {
    const next = !(ev.show_all_photo_tab === true || ev.show_all_photo_tab === 1);
    try {
      const res = await axios.patch(`${API_URL}/events/admin/${ev.id}/show-all-photo-tab`, { show_all_photo_tab: next });
      setEvents(events.map(e => e.id === ev.id ? { ...e, show_all_photo_tab: res.data.show_all_photo_tab } : e));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update');
    }
  };

  const handleBannerUpload = async (eventId, file) => {
    if (!file) return;
    setCardBanner((prev) => ({ ...prev, [eventId]: { uploading: true, error: null } }));
    try {
      const fd = new FormData();
      fd.append('banner', file);
      const res = await axios.post(`${API_URL}/events/admin/${eventId}/banner`, fd);
      setEvents((prev) => prev.map((ev) => ev.id === eventId ? { ...ev, banner_image: res.data.banner_image } : ev));
      setCardBanner((prev) => ({ ...prev, [eventId]: { uploading: false, error: null } }));
    } catch (err) {
      setCardBanner((prev) => ({ ...prev, [eventId]: { uploading: false, error: err.response?.data?.error || 'Upload failed' } }));
    }
  };

  const handleBannerRemove = async (eventId) => {
    setCardBanner((prev) => ({ ...prev, [eventId]: { uploading: true, error: null } }));
    try {
      await axios.delete(`${API_URL}/events/admin/${eventId}/banner`);
      setEvents((prev) => prev.map((ev) => ev.id === eventId ? { ...ev, banner_image: null } : ev));
      setCardBanner((prev) => ({ ...prev, [eventId]: { uploading: false, error: null } }));
    } catch (err) {
      setCardBanner((prev) => ({ ...prev, [eventId]: { uploading: false, error: err.response?.data?.error || 'Remove failed' } }));
    }
  };

  const copyText = (text) => { navigator.clipboard.writeText(text); };
  const baseUrl = window.location.origin;
  const verifyShareUrl = (token) => `${baseUrl}/verify/${token}`;
  // OG preview URL — used for social share buttons so crawlers see the event name + banner
  const verifyOgUrl = (token) => `${baseUrl}/preview/${token}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="text-center">
          <div className="w-12 h-12 rounded-2xl bg-violet-50 flex items-center justify-center mx-auto mb-3">
            <div className="w-6 h-6 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
          </div>
          <p className="text-slate-500 text-sm">Loading events…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Page header */}
      <div>
        <h1 className="text-xl font-bold text-slate-900">All Events</h1>
        <p className="text-sm text-slate-500 mt-0.5">{events.length} event{events.length !== 1 ? 's' : ''} total</p>
      </div>

      {error && (
        <div className="flex items-start gap-2.5 p-4 bg-rose-50 border border-rose-200 rounded-2xl">
          <svg className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-rose-700 text-sm">{error}</p>
        </div>
      )}

      {events.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <p className="text-slate-500 text-sm">No events yet. Users create events from their dashboard.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {events.map((ev) => {
            const uploadActive = ev.upload_link_active === true || ev.upload_link_active === 1;
            const verifyActive = ev.verify_link_active === true || ev.verify_link_active === 1;
            const showAllPhoto = ev.show_all_photo_tab === true || ev.show_all_photo_tab === 1;
            return (
              <div key={ev.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                {/* Card header */}
                <div className="px-4 pt-4 pb-3 border-b border-slate-100">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs text-slate-400 mb-0.5">
                        {ev.user_name || '—'}{ev.user_phone ? ` · ${ev.user_phone}` : ''}
                      </p>
                      <h3 className="text-base font-bold text-slate-900 truncate">{ev.name}</h3>
                      {ev.created_at && (
                        <p className="text-xs text-slate-400 mt-0.5">
                          {new Date(ev.created_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setDeleteConfirmEvent(ev)}
                      className="p-1.5 rounded-xl text-rose-500 hover:bg-rose-50 transition-colors flex-shrink-0"
                      title="Delete event"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>

                  {/* Stats row */}
                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    <span className="text-xs text-slate-500">{ev.batch_count || 0} batches · {ev.photo_count || 0} photos</span>
                    {ev.pin != null && ev.pin !== '' && (
                      <span className="inline-flex items-center gap-1 bg-violet-50 text-violet-700 px-2 py-0.5 rounded-lg text-xs font-mono font-semibold">
                        PIN: {String(ev.pin)}
                        <button type="button" onClick={() => copyText(String(ev.pin))} className="text-violet-500 hover:text-violet-700" title="Copy PIN">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                        </button>
                        <button type="button" onClick={() => { setChangePinEvent(ev); setChangePinValue(''); setChangePinError(''); }} className="text-violet-500 hover:text-violet-700" title="Change PIN">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                        </button>
                      </span>
                    )}
                  </div>
                </div>

                {/* Banner */}
                {ev.banner_image ? (
                  <div className="relative group">
                    <img
                      src={ev.banner_image}
                      alt="Event banner"
                      className="w-full object-cover"
                      style={{ maxHeight: '140px' }}
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => cardBannerRefs.current[ev.id]?.click()}
                        disabled={cardBanner[ev.id]?.uploading}
                        className="px-3 py-1.5 rounded-xl bg-white/90 text-xs font-semibold text-slate-700 hover:bg-white transition-colors"
                      >Change</button>
                      <button
                        type="button"
                        onClick={() => handleBannerRemove(ev.id)}
                        disabled={cardBanner[ev.id]?.uploading}
                        className="px-3 py-1.5 rounded-xl bg-rose-500/90 text-xs font-semibold text-white hover:bg-rose-600 transition-colors"
                      >Remove</button>
                    </div>
                    {cardBanner[ev.id]?.uploading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="px-4 pt-1 pb-2">
                    <button
                      type="button"
                      onClick={() => cardBannerRefs.current[ev.id]?.click()}
                      disabled={cardBanner[ev.id]?.uploading}
                      className="w-full h-14 border border-dashed border-slate-200 rounded-xl flex items-center justify-center gap-2 text-xs text-slate-400 hover:border-violet-300 hover:text-violet-500 hover:bg-violet-50/30 transition-colors"
                    >
                      {cardBanner[ev.id]?.uploading ? (
                        <div className="w-4 h-4 border-2 border-violet-400 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          Add banner photo
                        </>
                      )}
                    </button>
                    {cardBanner[ev.id]?.error && (
                      <p className="text-xs text-rose-500 mt-1 text-center">{cardBanner[ev.id].error}</p>
                    )}
                  </div>
                )}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  ref={(el) => { cardBannerRefs.current[ev.id] = el; }}
                  onChange={(e) => handleBannerUpload(ev.id, e.target.files?.[0])}
                />

                {/* Links */}
                <div className="px-4 py-3 space-y-2.5">
                  {/* Upload link */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-slate-600">Upload (PIN required)</span>
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold ${uploadActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        <span className={`w-1 h-1 rounded-full ${uploadActive ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                        {uploadActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <code className="flex-1 bg-slate-50 border border-slate-200 px-2 py-1 rounded-lg text-[11px] text-slate-600 truncate">
                        {baseUrl}/upload/{ev.upload_token}
                      </code>
                      <button type="button" onClick={() => copyText(`${baseUrl}/upload/${ev.upload_token}`)} className="p-1.5 rounded-lg text-slate-400 hover:text-violet-600 hover:bg-violet-50 transition-colors" title="Copy">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                      </button>
                    </div>
                  </div>

                  {/* Verify link */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-slate-600">Verify (no PIN)</span>
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold ${verifyActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        <span className={`w-1 h-1 rounded-full ${verifyActive ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                        {verifyActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <code className="flex-1 bg-slate-50 border border-slate-200 px-2 py-1 rounded-lg text-[11px] text-slate-600 truncate">
                        {verifyOgUrl(ev.verify_token)}
                      </code>
                      <button type="button" onClick={() => copyText(verifyOgUrl(ev.verify_token))} className="p-1.5 rounded-lg text-slate-400 hover:text-violet-600 hover:bg-violet-50 transition-colors" title="Copy">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                      </button>
                    </div>

                    {/* Share icons */}
                    <div className="flex items-center gap-1 mt-1.5">
                      <span className="text-[10px] text-slate-400 mr-0.5">Share:</span>
                      <a href={`https://wa.me/?text=${encodeURIComponent(verifyOgUrl(ev.verify_token))}`} target="_blank" rel="noopener noreferrer" className="p-1 rounded-lg text-green-600 hover:bg-green-50 transition-colors" title="WhatsApp">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      </a>
                      <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(verifyOgUrl(ev.verify_token))}`} target="_blank" rel="noopener noreferrer" className="p-1 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors" title="Facebook">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                      </a>
                      <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(verifyOgUrl(ev.verify_token))}&text=${encodeURIComponent('Find my photos')}`} target="_blank" rel="noopener noreferrer" className="p-1 rounded-lg text-sky-500 hover:bg-sky-50 transition-colors" title="X (Twitter)">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                      </a>
                    </div>
                  </div>

                  {/* Toggle */}
                  <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                    <label htmlFor={`all-photo-${ev.id}`} className="text-xs text-slate-600 cursor-pointer select-none">
                      Show &quot;All Photos&quot; tab on verify page
                    </label>
                    <button
                      id={`all-photo-${ev.id}`}
                      type="button"
                      onClick={() => toggleShowAllPhotoTab(ev)}
                      className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${showAllPhoto ? 'bg-violet-600' : 'bg-slate-200'}`}
                      role="switch"
                      aria-checked={showAllPhoto}
                    >
                      <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform transition duration-200 ${showAllPhoto ? 'translate-x-4' : 'translate-x-0'}`} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete confirm modal */}
      {deleteConfirmEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDeleteConfirmEvent(null)} />
          <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900">Delete event?</h3>
                <p className="text-sm text-slate-500 mt-1">
                  &ldquo;{deleteConfirmEvent.name}&rdquo; and all its {deleteConfirmEvent.batch_count || 0} batches / {deleteConfirmEvent.photo_count || 0} photos will be permanently deleted.
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setDeleteConfirmEvent(null)} disabled={deleting}
                className="flex-1 px-4 py-2 border border-slate-200 rounded-xl text-slate-700 hover:bg-slate-50 text-sm font-medium transition-colors">
                Cancel
              </button>
              <button type="button" onClick={handleDeleteEvent} disabled={deleting}
                className="flex-1 px-4 py-2 bg-rose-600 text-white rounded-xl hover:bg-rose-700 disabled:opacity-50 text-sm font-semibold transition-colors">
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change PIN modal */}
      {changePinEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => { setChangePinEvent(null); setChangePinValue(''); setChangePinError(''); }} />
          <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
            <h3 className="text-base font-bold text-slate-900 mb-1">Change PIN</h3>
            <p className="text-sm text-slate-500 mb-4">{changePinEvent.name}</p>
            {changePinError && (
              <p className="text-rose-600 text-sm mb-3">{changePinError}</p>
            )}
            <form onSubmit={handleChangePin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">New 6-digit PIN</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={changePinValue}
                  onChange={(e) => setChangePinValue(e.target.value.replace(/\D/g, ''))}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm text-center tracking-widest font-mono focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                  placeholder="123456"
                  autoFocus
                />
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => { setChangePinEvent(null); setChangePinValue(''); setChangePinError(''); }}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-xl text-slate-700 hover:bg-slate-50 text-sm font-medium">
                  Cancel
                </button>
                <button type="submit"
                  className="flex-1 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all"
                  style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>
                  Update PIN
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEvents;
